"use client";
import { motion } from "framer-motion";
import { fadeUp, stagger } from "../../../../components/motionVariants";
import { ArrowRight } from "lucide-react";
import Link from "next/link";
import DocsPageFrame from "../../../components/DocsPageFrame";
import Callout from "../../../components/Callout";
import Screenshot from "../../../components/Screenshot";

const RULES = [
  {
    topic: "No subscription gate",
    rule: "Creating an Item Group or a Sub Item Group isn't limited by your plan",
  },
  {
    topic: "Unique name",
    rule: "Both Item Group names and Sub Item Group names must be unique per company, checked case-insensitively, but the two lists don't check against each other",
  },
  {
    topic: "Item Group is required on every item",
    rule: "You can't save an item without picking one. Sub Item Group is optional and can be left blank",
  },
  {
    topic: "No active/inactive status",
    rule: "Neither Item Group nor Sub Item Group has a deactivate action, only Edit and Delete",
  },
  {
    topic: "Deleting an Item Group",
    rule: "Blocked if any Item still has this group set",
  },
  {
    topic: "Deleting a Sub Item Group",
    rule: "Blocked the same way, checked separately against the same Item collection",
  },
  {
    topic: "Same permission, two lists",
    rule: "Item Group and Sub Item Group are controlled by the same role permission, even though they're unrelated records",
  },
];

const sections = [
  {
    heading: "What this module does",
    subsections: [
      { heading: "1. An Item Group is the required category every item belongs to" },
      { heading: "2. The item count and Associated Items list are computed live" },
      { heading: "3. Sub Item Group is a second, independent tag, not a child of Item Group" },
    ],
  },
  { heading: "Business rules" },
];

const Definitions = ({ items }) => (
  <div className="space-y-2.5">
    {items.map((item) => (
      <p key={item.term} className="text-[16px] geist-regular text-[#374151] leading-relaxed">
        <span className="geist-medium text-[#111827]">{item.term}:</span> {item.desc}
      </p>
    ))}
  </div>
);

export default function ItemGroupPage() {
  return (
    <DocsPageFrame sections={sections}>
      <motion.div
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.05 }}
        variants={stagger}
      >
        {/* Header */}
        <motion.div variants={fadeUp} className="mb-10">
          <h1 className="text-[30px] leading-[1.2] text-[#111827] sentient-bold mb-4">
            Item Group
          </h1>
          <h2 className="text-[24px] sentient-medium text-[#111827] mb-3">
            For: Anyone organizing items into categories for reporting and pricing
          </h2>
          <p className="text-[16px] sm:text-[17px] leading-[1.75] text-[#374151] geist-regular max-w-2xl">
            An <span className="geist-medium text-[#111827]">Item Group</span>{" "}
            is a named category, Raw Materials, Finished Goods, Packaging,
            whatever your company uses, that every item is required to
            belong to. This same screen also has a second, easy-to-miss tab
            called{" "}
            <span className="geist-medium text-[#111827]">Sub Item Group</span>,
            which despite the name isn&apos;t a child of Item Group at all.
          </p>
        </motion.div>

        <hr className="my-8" />

        {/* What this module does */}
        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-0"
            className="text-[24px] sentient-medium text-[#111827] mb-6 scroll-mt-24"
          >
            What this module does
          </h2>

          {/* 1. Required category */}
          <h3
            id="section-0-sub-0"
            className="text-[18px] sentient-medium text-[#111827] mb-3 scroll-mt-24"
          >
            1. An Item Group is the required category every item belongs to
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            The Add Item Group form is as small as it looks, just a name.
          </p>
          <Definitions
            items={[
              {
                term: "Item Group Id",
                desc: "Assigned automatically with an IG prefix.",
              },
              {
                term: "Name",
                desc: "Must be unique across your company, checked without regard to letter case.",
              },
              {
                term: "On the Item itself",
                desc: "Item Group is a required field. An item cannot be saved without one.",
              },
            ]}
          />
          <Screenshot caption="Screenshot: the Add Item Group form, showing just the auto-generated code and a name." />

          {/* 2. Live item count */}
          <h3
            id="section-0-sub-1"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            2. The item count and Associated Items list are computed live
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            This isn&apos;t a stored number that can drift out of date, it&apos;s
            worked out fresh every time you look.
          </p>
          <Definitions
            items={[
              {
                term: "On the list screen",
                desc: "Every row shows how many items currently carry that group, calculated by scanning Item Master at the moment the list loads.",
              },
              {
                term: "On the View screen",
                desc: "Opening a group shows the same count plus a full table of every item assigned to it, with each item's number, name, type, and status.",
              },
              {
                term: "Nothing here is editable",
                desc: "This view is read-only. To move an item out of a group, you edit that item in Item Master.",
              },
            ]}
          />
          <Screenshot caption="Screenshot: an Item Group's View page, showing its live item count and the Associated Items table." />

          {/* 3. Sub Item Group */}
          <h3
            id="section-0-sub-2"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            3. Sub Item Group is a second, independent tag, not a child of Item Group
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            The Sub Item Group tab sits right next to Item Group on the same
            page, but the two records have no relationship to each other.
          </p>
          <Definitions
            items={[
              {
                term: "Its own list, its own name",
                desc: "A Sub Item Group is just a name, with the same case-insensitive uniqueness check as Item Group, but checked only against other Sub Item Groups.",
              },
              {
                term: "Optional on the item",
                desc: "An item's Sub Item Group field can be left blank, unlike Item Group which is mandatory.",
              },
              {
                term: "Not nested",
                desc: "A Sub Item Group doesn't belong to, or point back at, any specific Item Group. It's a completely separate second category an item can optionally carry.",
              },
              {
                term: "Same permission",
                desc: "Creating, viewing, editing, or deleting a Sub Item Group is controlled by the same role permission as Item Group.",
              },
            ]}
          />
          <Callout type="note">
            If you&apos;re expecting Sub Item Group to narrow down an Item
            Group, for example Raw Materials broken into Metals and
            Plastics, that&apos;s not how it works today. Both fields sit on
            the item side by side, independently of each other.
          </Callout>
          <Screenshot caption="Screenshot: the Sub Item Group tab, showing its own separate Add form and list." />
        </motion.div>

        <hr className="my-8" />

        {/* Business rules */}
        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-1"
            className="text-[24px] sentient-medium text-[#111827] mb-4 scroll-mt-24"
          >
            Business rules
          </h2>
          <div className="border border-[#E5E5E5] rounded-[14px] overflow-hidden bg-white overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-[#F5F5F5] bg-[#F9F9F9]">
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    Topic
                  </th>
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    Rule
                  </th>
                </tr>
              </thead>
              <tbody>
                {RULES.map((r, i) => (
                  <tr key={r.topic} className={i < RULES.length - 1 ? "border-b border-[#F5F5F5]" : ""}>
                    <td className="px-5 py-3 text-[14px] geist-medium text-[#111827] align-top w-[220px]">
                      {r.topic}
                    </td>
                    <td className="px-5 py-3 text-[14px] geist-regular text-[#374151]">{r.rule}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>

        {/* Related */}
        <motion.div variants={fadeUp} className="flex flex-col sm:flex-row gap-4">
          <Link
            href="/documentation/books/all-master/item-master"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] transition-colors mb-1 flex items-center gap-1.5">
              Item Master <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#6B7280]">
              Where every item picks its Item Group and Sub Item Group
            </p>
          </Link>
          <Link
            href="/documentation/books/all-master/price-list"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] transition-colors mb-1 flex items-center gap-1.5">
              Price List <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#6B7280]">
              Uses Item Group to narrow the item picker on each price line
            </p>
          </Link>
        </motion.div>
      </motion.div>
    </DocsPageFrame>
  );
}
